import { useMutation, useQuery } from "@apollo/client";
import { compact, max, omit } from "lodash";
import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Prompt } from "react-router-dom";
import {
  CompetencyAppliesTo,
  CompetencyCriteriaInput,
  CompetencyCriteriaUniqueness,
  DeleteOrgWideCompetencyMutation,
  DeleteOrgWideCompetencyMutationVariables,
  GetOrgWideCompetencyQuery,
  GetOrgWideCompetencyQueryVariables,
  SaveOrgWideCompetencyMutation,
  SaveOrgWideCompetencyMutationVariables,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import TeamEditDialog from "@apps/org-settings/components/team-edit-dialog";
import createOrUpdateOrgWideCompetencyMutation from "@apps/org-settings/graphql/create-or-update-org-wide-competency-mutation";
import deleteOrgWideCompetencyMutation from "@apps/org-settings/graphql/delete-org-wide-competency-mutation";
import getOrganizationCompetencyQuery from "@apps/org-settings/graphql/get-organization-competency-query";
import TeamPicker, {
  TeamPickerTeam,
} from "@apps/reporting/components/team-picker";
import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import ModalTitle from "@components/modal/modal-title";
import MotionDiv from "@components/motion/motion-div";
import { ToggleButtonGroup } from "@components/toggle-button-group/toggle-button-group";
import { ToggleButtonGroupTheme } from "@components/toggle-button-group/toggle-button-group-constants";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import AddButton from "./add-button";
import CareerTrackCreateModal from "./career-track-create-modal";
import CareerTrackPicker, {
  CareerTrackPickerTrack,
} from "./career-track-picker";
import CriteriaEditor from "./criteria-editor";
import {
  CompetencyRoleTypeScope,
  CompetencyScopeKind,
  competencyRoleTypeScopeFlags,
  getCompetencyAppliesToOnSave,
  getCompetencyAudienceDescription,
  getCompetencyRoleTypeScope,
  getCompetencyScopeKind,
  leaveBlankUnsaved,
  roleLevelIterator,
} from "./helpers";
import usePendingDeletes from "./use-pending-deletes";

const Controls = ({
  isSaving,
  onCancel,
  canSave,
  onSave,
  size,
  saveTooltip,
}: {
  isSaving: boolean;
  onCancel: () => void;
  canSave: boolean;
  onSave: () => void;
  size?: ButtonSize;
  saveTooltip?: string | null;
}) => {
  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        disabled={isSaving}
        size={size}
        theme={buttonTheme.text}
        text="Discard"
        onClick={onCancel}
      />
      <Button
        disabled={isSaving || !canSave}
        tooltip={saveTooltip}
        size={size}
        theme="primary"
        text="Save"
        onClick={onSave}
      />
    </div>
  );
};

const CompetencyForm = ({
  organizationId,
  competencyId,
  onCompetencySaved,
  onCancel,
  startingData,
  startingTeams,
  startingCareerTracks,
  showSharedWarning,
  onCompetencyDeleted,
  titleInputRef,
  onDirtyChange,
  onDismiss,
}: {
  onCancel: () => void;
  onCompetencySaved: (competencyId: number) => void;
  organizationId: number;
  competencyId?: number | null;
  startingData?: Partial<
    Omit<SaveOrgWideCompetencyMutationVariables, "criteria" | "appliesTo">
  >;
  // Scope a new competency starts with, so adding one from a section of the
  // career track table keeps that section's reach.
  startingTeams?: TeamPickerTeam[];
  startingCareerTracks?: CareerTrackPickerTrack[];
  // Warn that edits reach other career tracks, when the saved competency is
  // shared beyond a single track.
  showSharedWarning?: boolean;
  // Enables Delete in the modal footer.
  onCompetencyDeleted?: () => void;
  // Lets a modal put the cursor in the name field when it opens.
  titleInputRef?: RefObject<HTMLInputElement>;
  // Lets the modal ask before closing over unsaved changes.
  onDirtyChange?: (isDirty: boolean) => void;
  // The title's close button. Discard and Delete still close through onCancel.
  onDismiss?: () => void;
}) => {
  const label = useLabel();
  const {
    confirm: confirmDeleteCompetency,
    ConfirmationDialog: ConfirmDeleteCompetencyDialog,
  } = useConfirm(
    "Delete?",
    `Are you sure you want to delete this ${label(
      "competency",
    )}? It will be removed from every career track that uses it.`,
  );
  const [deleteCompetency, { loading: isDeleting }] = useMutation<
    DeleteOrgWideCompetencyMutation,
    DeleteOrgWideCompetencyMutationVariables
  >(deleteOrgWideCompetencyMutation, {
    onCompleted: () => {
      successNotificationVar({
        title: `${label("competency", { capitalize: true })} deleted`,
        timeout: 2000,
      });
      onCompetencyDeleted?.();
      onCancel();
    },
  });

  const handleDeleteCompetency = useCallback(async () => {
    if (!competencyId || !(await confirmDeleteCompetency())) {
      return;
    }
    deleteCompetency({ variables: { competencyId } });
  }, [competencyId, confirmDeleteCompetency, deleteCompetency]);
  const errorMatches = useMemo(
    () => [
      {
        match: "A competency with that title already exists",
        title: `A ${label(
          "competency",
        )} with that title already exists, please choose another title.`,
      },
    ],
    [label],
  );

  const [isFormDirty, setIsFormDirty] = useState(false);
  useEffect(() => {
    onDirtyChange?.(isFormDirty);
  }, [isFormDirty, onDirtyChange]);

  const [levelCount, setLevelCount] = useState(1);

  const isNew = competencyId === null || competencyId === undefined;

  const [competencyData, setCompetencyData] = useState<
    Omit<SaveOrgWideCompetencyMutationVariables, "criteria" | "appliesTo">
  >({
    title: "",
    description: "",
    criteriaUniqueness: CompetencyCriteriaUniqueness.SameAcrossRoles,
    appliesToIcRoles: false,
    appliesToManagementRoles: false,
    competencyId,
    organizationId,
    appliesToTeams: (startingTeams ?? []).map(({ id }) => id),
    appliesToCareerTracks: (startingCareerTracks ?? []).map(({ id }) => id),
    ...startingData,
  });
  const [appliesToTeams, setAppliesToTeams] = useState<TeamPickerTeam[]>(
    startingTeams ?? [],
  );
  const [appliesToCareerTracks, setAppliesToCareerTracks] = useState<
    CareerTrackPickerTrack[]
  >(startingCareerTracks ?? []);
  // Which of the three the form is asking about. It follows the saved
  // competency once it loads, and otherwise whatever the user chose.
  const [scopeKind, setScopeKind] = useState<CompetencyScopeKind>(
    getCompetencyScopeKind({
      teamCount: (startingTeams ?? []).length,
      careerTrackCount: (startingCareerTracks ?? []).length,
    }),
  );
  // Each scope tab's picks as it was left, to bring back on return.
  const tabPicksRef = useRef<
    Partial<
      Record<
        CompetencyScopeKind,
        { teams: TeamPickerTeam[]; tracks: CareerTrackPickerTrack[] }
      >
    >
  >({});
  // Whether the role type, departments or career tracks were edited, which is
  // when the saved "all roles" or "some roles" is worked out again.
  const [isReachChanged, setIsReachChanged] = useState(false);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [isCreatingCareerTrack, setIsCreatingCareerTrack] = useState(false);
  const [criteria, setCriteria] = useState<
    (CompetencyCriteriaInput & { uuid: string })[]
  >([]);
  // Criteria a bin button took out, for Save to send: a save deletes only the
  // ids it names.
  const pendingDeletes = usePendingDeletes();

  const { data, loading } = useQuery<
    GetOrgWideCompetencyQuery,
    GetOrgWideCompetencyQueryVariables
  >(getOrganizationCompetencyQuery, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables: {
      competencyId: competencyId ?? 0,
    },
    skip: isNew,
    context: { errorMatches },
    onCompleted: (data) => {
      const criteria = assertEdgesNonNull(data?.competency?.criteria);
      setCompetencyData({
        ...data.competency,
        appliesToTeams: assertEdgesNonNull(data.competency?.appliesToTeams).map(
          ({ id }) => id,
        ),
        appliesToCareerTracks: assertEdgesNonNull(
          data.competency?.appliesToCareerTracks,
        ).map(({ id }) => id),
      });
      setAppliesToTeams(assertEdgesNonNull(data.competency?.appliesToTeams));
      const teams = assertEdgesNonNull(data.competency?.appliesToTeams);
      const tracks = assertEdgesNonNull(data.competency?.appliesToCareerTracks);
      setAppliesToCareerTracks(tracks);
      setScopeKind(
        getCompetencyScopeKind({
          teamCount: teams.length,
          careerTrackCount: tracks.length,
        }),
      );
      setCriteria(
        criteria.map((criteria) => ({
          level: criteria.level,
          content: criteria.content,
          id: criteria.id,
          uuid: uuidv4(),
        })),
      );
      // A competency may have no criteria yet; start it at level 1.
      setLevelCount(max(criteria.map((criteria) => criteria.level)) ?? 1);
    },
  });

  const [saveCompetency, { loading: isSaving }] = useMutation<
    SaveOrgWideCompetencyMutation,
    SaveOrgWideCompetencyMutationVariables
  >(createOrUpdateOrgWideCompetencyMutation, {
    // Saving clears the dirty flag up front; restore it if the save fails.
    onError: () => setIsFormDirty(true),
    onCompleted: (response) => {
      successNotificationVar({
        title: `${label("competency", { capitalize: true })} saved`,
      });
      onCompetencySaved(
        assertNonNull(response.createOrUpdateCompetency?.competency?.id),
      );
    },
  });

  const roleTypeScope = getCompetencyRoleTypeScope(competencyData);
  // Everything the competency is narrowed to, in one place: the form asks for
  // the narrowing and works out whether that leaves all roles or some.
  const appliesTo = getCompetencyAppliesToOnSave({
    roleTypeScope,
    teamCount: appliesToTeams.length,
    careerTrackCount: appliesToCareerTracks.length,
    savedAppliesTo: data?.competency?.appliesTo,
    isReachChanged,
  });

  const audienceDescription = getCompetencyAudienceDescription({
    roleTypeScope,
    teamTitles: appliesToTeams.map(({ title }) => title),
    careerTrackTitles: appliesToCareerTracks.map(({ title }) => title),
    label,
  });

  const handleSaveCompetency = useCallback(async () => {
    setIsFormDirty(false);
    // Per role, the criteria are the roles' own, which this form doesn't
    // hold. Switching to it clears the shared ones on the server.
    const isPerRole =
      competencyData.criteriaUniqueness ===
      CompetencyCriteriaUniqueness.DifferentForEachRole;
    const sentDeleteIds = isPerRole ? [] : pendingDeletes.get("criteria");
    const response = await saveCompetency({
      variables: {
        ...competencyData,
        // Only when changed here: switching kind deletes the other kind's
        // criteria, so a dialog opened before someone else switched it
        // mustn't switch it back.
        criteriaUniqueness:
          isNew ||
          competencyData.criteriaUniqueness !==
            data?.competency?.criteriaUniqueness
            ? competencyData.criteriaUniqueness
            : undefined,
        appliesTo,
        // A cleared one keeps its saved text (see leaveBlankUnsaved).
        criteria: isPerRole
          ? undefined
          : leaveBlankUnsaved(criteria).map((item) => omit(item, "uuid")),
        deleteCriteriaIds: isPerRole ? undefined : sentDeleteIds,
        competencyId: competencyId,
        appliesToTeams: appliesToTeams.map(({ id }) => id),
        appliesToCareerTracks: appliesToCareerTracks.map(({ id }) => id),
      },
      context: { errorMatches },
    });
    if (response.data?.createOrUpdateCompetency) {
      pendingDeletes.remove("criteria", sentDeleteIds);
    }
  }, [
    appliesTo,
    appliesToTeams,
    appliesToCareerTracks,
    competencyData,
    competencyId,
    criteria,
    data,
    errorMatches,
    isNew,
    pendingDeletes,
    saveCompetency,
  ]);

  const saveTooltip = !competencyData.title?.trim()
    ? "Please enter a name"
    : scopeKind === "department" && appliesToTeams.length === 0
      ? `Pick at least one ${label("team")}`
      : scopeKind === "track" && appliesToCareerTracks.length === 0
        ? "Pick at least one career track"
        : !isNew && !isFormDirty
          ? "No changes to save"
          : null;
  const canSave = !saveTooltip;

  const savedCompetency = data?.competency;
  const isSharedAcrossCareerTracks =
    !!savedCompetency &&
    (savedCompetency.appliesTo === CompetencyAppliesTo.AllRoles ||
      !!savedCompetency.appliesToIcRoles ||
      !!savedCompetency.appliesToManagementRoles ||
      assertEdgesNonNull(savedCompetency.appliesToTeams).length > 0 ||
      assertEdgesNonNull(savedCompetency.appliesToCareerTracks).length > 1);

  // Enter in the name commits, the way a dialog's default action does. Only
  // there: elsewhere Enter belongs to the focused button, picker or editor.
  const handleTitleKeyDown = (evt: KeyboardEvent<HTMLInputElement>) => {
    if (evt.key !== "Enter" || evt.shiftKey || evt.nativeEvent.isComposing) {
      return;
    }
    evt.preventDefault();
    if (canSave && !isSaving) {
      handleSaveCompetency();
    }
  };

  const formBody = (
    <div>
      {showSharedWarning && isSharedAcrossCareerTracks && (
        <Callout theme="warning" icon="warning" size="small" className="mb-4">
          {`This ${label("competency")} is shared across multiple career tracks. Changes you make here will apply to all of them, not just this one.`}
        </Callout>
      )}
      <div className="flex flex-col gap-6">
        <div className="flex items-center">
          <div className="shrink-0 text-gray-500 text-xs uppercase font-semibold w-48">
            Name
          </div>
          <Input
            ref={titleInputRef}
            aria-label="Competency title input"
            disabled={isSaving}
            className="flex-1 h-[24px] px-2"
            value={competencyData.title ?? ""}
            onKeyDown={handleTitleKeyDown}
            onChange={(evt) => {
              setIsFormDirty(true);
              setCompetencyData({
                ...competencyData,
                title: evt.target.value,
              });
            }}
          />
        </div>
        <div className="flex items-start">
          <div className="shrink-0 mt-2.5 text-gray-500 text-xs uppercase font-semibold w-48">
            Description
          </div>
          <div className="flex-1 min-w-0">
            <TextareaWysiwyg
              editable={!isSaving}
              className="bg-white"
              value={competencyData.description}
              deps={[competencyData.title]}
              onChangeValue={(description) => {
                setIsFormDirty(true);
                setCompetencyData({
                  ...competencyData,
                  description,
                });
              }}
            />
          </div>
        </div>
        {/* One block: the competency is narrowed by role type, by department
            and by career track, and the sentence underneath reads the
            selections back so its reach doesn't have to be inferred. */}
        <div className="flex items-start">
          <div className="shrink-0 text-gray-500 text-xs uppercase font-semibold w-48 mt-1.5">
            Applies to
          </div>
          {/* No row labels: the tabs and pickers name themselves, and the
              sentence above them says what the choices add up to. */}
          <div className="flex flex-1 flex-col gap-4 items-start">
            {/* Role type and scope are two halves of one sentence, so they
                sit together: who it is for, and where they are. */}
            <div className="flex items-center flex-wrap gap-2">
              <ToggleButtonGroup
                theme={ToggleButtonGroupTheme.defaultButtons}
                size={ButtonSize.medium}
                aria-label="Competency role types tabs"
                buttons={[
                  {
                    title: "All roles",
                    value: "all",
                    active: roleTypeScope === "all",
                    disabled: isSaving,
                  },
                  {
                    title: "Managers",
                    value: "management",
                    active: roleTypeScope === "management",
                    disabled: isSaving,
                  },
                  {
                    title: "ICs",
                    value: "ic",
                    active: roleTypeScope === "ic",
                    disabled: isSaving,
                  },
                ]}
                onClick={(button) => {
                  if (button.value === roleTypeScope) {
                    return;
                  }
                  setIsFormDirty(true);
                  setIsReachChanged(true);
                  setCompetencyData({
                    ...competencyData,
                    ...competencyRoleTypeScopeFlags(
                      button.value as CompetencyRoleTypeScope,
                    ),
                  });
                }}
              />
              {/* Reads as one sentence: who, then where. Without the word
                  between them the six buttons look like one group, and
                  "everyone" and "all roles" look like the same choice. */}
              <span className="text-gray-500 shrink-0 mx-2">in</span>
              <ToggleButtonGroup
                theme={ToggleButtonGroupTheme.defaultButtons}
                size={ButtonSize.medium}
                aria-label="Competency scope tabs"
                buttons={[
                  {
                    // A place rather than a population: the group before it
                    // has already said who.
                    title: `The ${label("organization")}`,
                    value: "everyone",
                    active: scopeKind === "everyone",
                    disabled: isSaving,
                  },
                  {
                    // Plural: more than one can be picked.
                    title: label("team", { capitalize: true, pluralize: true }),
                    value: "department",
                    active: scopeKind === "department",
                    disabled: isSaving,
                  },
                  {
                    title: "Career tracks",
                    value: "track",
                    active: scopeKind === "track",
                    disabled: isSaving,
                  },
                ]}
                onClick={(button) => {
                  // The active tab again keeps its picks.
                  if (button.value === scopeKind) {
                    return;
                  }
                  setIsFormDirty(true);
                  setIsReachChanged(true);
                  // Switching scope drops the other tabs' picks, which is the
                  // whole point of asking for one. Never the picks of the tab
                  // switched to: a department competency can also have been
                  // added to a track by hand, and that track stays.
                  const nextScopeKind = button.value as CompetencyScopeKind;
                  // Coming back to a tab brings back what it had, so looking
                  // at the tabs never loses a pick (or a saved one).
                  const cached = tabPicksRef.current[nextScopeKind];
                  tabPicksRef.current[scopeKind] = {
                    teams: appliesToTeams,
                    tracks: appliesToCareerTracks,
                  };
                  if (cached) {
                    setAppliesToTeams(cached.teams);
                    setAppliesToCareerTracks(cached.tracks);
                  } else {
                    if (nextScopeKind !== "department") {
                      setAppliesToTeams([]);
                    }
                    if (nextScopeKind !== "track") {
                      setAppliesToCareerTracks([]);
                    }
                  }
                  setScopeKind(nextScopeKind);
                }}
              />
            </div>
            {scopeKind === "department" && (
              <div className="flex items-center gap-4">
                <TeamPicker
                  size={ButtonSize.medium}
                  canChange
                  onClickCreateNew={() => setIsCreatingTeam(true)}
                  teams={appliesToTeams}
                  onAddTeam={(team) => {
                    setIsFormDirty(true);
                    setIsReachChanged(true);
                    setAppliesToTeams([...appliesToTeams, team]);
                  }}
                  onRemoveTeam={(team) => {
                    setIsFormDirty(true);
                    setIsReachChanged(true);
                    setAppliesToTeams(
                      appliesToTeams.filter((t) => t.id !== team.id),
                    );
                  }}
                />
              </div>
            )}
            {scopeKind === "track" && (
              <div className="flex items-center gap-4">
                <CareerTrackPicker
                  size={ButtonSize.medium}
                  canChange
                  onClickCreateNew={() => setIsCreatingCareerTrack(true)}
                  careerTracks={appliesToCareerTracks}
                  onAddCareerTrack={(track) => {
                    setIsFormDirty(true);
                    setIsReachChanged(true);
                    setAppliesToCareerTracks([...appliesToCareerTracks, track]);
                  }}
                  onRemoveCareerTrack={(track) => {
                    setIsFormDirty(true);
                    setIsReachChanged(true);
                    setAppliesToCareerTracks(
                      appliesToCareerTracks.filter((t) => t.id !== track.id),
                    );
                  }}
                />
              </div>
            )}
            {/* The selections read back in a line, under the controls that
                produced them. */}
            <div
              aria-label="Competency audience"
              className="rounded-md bg-amber-50 px-3 py-2 text-amber-900"
            >
              {audienceDescription}
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <div className="shrink-0 text-gray-500 text-xs uppercase font-semibold w-48">
            Criteria for each level
          </div>
          <ToggleButtonGroup
            theme={ToggleButtonGroupTheme.defaultButtons}
            size={ButtonSize.medium}
            aria-label="Competency criteria uniqueness tabs"
            buttons={[
              {
                title: "Same across roles",
                value: CompetencyCriteriaUniqueness.SameAcrossRoles,
                active:
                  competencyData.criteriaUniqueness ===
                  CompetencyCriteriaUniqueness.SameAcrossRoles,
                disabled: isSaving,
              },
              {
                title: "Unique across roles",
                value: CompetencyCriteriaUniqueness.DifferentForEachRole,
                active:
                  competencyData.criteriaUniqueness ===
                  CompetencyCriteriaUniqueness.DifferentForEachRole,
                disabled: isSaving,
              },
            ]}
            onClick={(button) => {
              setIsFormDirty(true);
              setCompetencyData({
                ...competencyData,
                criteriaUniqueness:
                  button.value as CompetencyCriteriaUniqueness,
              });
            }}
          />
        </div>
      </div>

      {competencyData.criteriaUniqueness ===
      CompetencyCriteriaUniqueness.SameAcrossRoles ? (
        <MotionDiv
          key="criteria"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="font-bold mt-8">
            Edit criteria here, or in the career track table
          </div>
          <div className="border rounded-md mt-4">
            {roleLevelIterator(levelCount).map((level) => (
              <div className="flex border-b last:border-b-0" key={level}>
                <div className="bg-gray-100 p-4 font-semibold border-r w-48">
                  Level {level}
                </div>
                <div className="p-4 flex-1">
                  <CriteriaEditor
                    disabled={isSaving}
                    editable
                    placeholder={`Enter ${label("competency")} criteria`}
                    deleteConfirmation={{
                      title: `Delete ${label("competency")} criteria?`,
                      message: `Are you sure you want to delete this criteria? It's removed from every role this ${label(
                        "competency",
                      )} applies to.`,
                    }}
                    onAddCriteria={() => {
                      setIsFormDirty(true);
                      setCriteria([
                        ...criteria,
                        { level, content: "", uuid: uuidv4() },
                      ]);
                    }}
                    onPasteMultipleCriteria={(items) => {
                      setIsFormDirty(true);
                      const updatingCriteriaUuids = compact(
                        items.map(({ uuid }) => uuid),
                      );
                      setCriteria([
                        ...criteria.filter(
                          (criteria) =>
                            !updatingCriteriaUuids.includes(criteria.uuid),
                        ),
                        // Pasted over a saved one: keep it (its id), so the
                        // save updates it rather than adding another.
                        ...items.map((item) => ({
                          ...criteria.find(({ uuid }) => uuid === item.uuid),
                          level,
                          content: item.content,
                          uuid: item.uuid || uuidv4(),
                        })),
                      ]);
                    }}
                    onDeleteCriteria={(uuid) => {
                      setIsFormDirty(true);
                      pendingDeletes.add("criteria", [
                        {
                          uuid,
                          id: criteria.find((item) => item.uuid === uuid)?.id,
                        },
                      ]);
                      setCriteria((prev) =>
                        prev.filter((criteria) => criteria.uuid !== uuid),
                      );
                    }}
                    onCriteriaChange={(uuid, content) => {
                      setIsFormDirty(true);
                      setCriteria((prev) =>
                        prev.map((item) => {
                          if (item.uuid === uuid) {
                            return {
                              ...item,
                              content,
                            };
                          }
                          return item;
                        }),
                      );
                    }}
                    criteria={criteria.filter((c) => c.level === level)}
                  />
                </div>
              </div>
            ))}
          </div>
          <AddButton
            className="mt-4"
            size={ButtonSize.medium}
            leftNegativeMargin
            disabled={isSaving}
            text="Add Level"
            onClick={() => setLevelCount((levelCount) => levelCount + 1)}
          />
        </MotionDiv>
      ) : (
        <MotionDiv
          initial={{ opacity: 0 }}
          key="no-criteria"
          animate={{ opacity: 1 }}
          className="flex justify-center mt-10"
        >
          <div className="text-gray-500">
            {label("competency", { capitalize: true })} criteria will be defined
            on the role
          </div>
        </MotionDiv>
      )}
    </div>
  );

  return (
    <>
      <ConfirmDeleteCompetencyDialog />
      <Prompt
        when={isFormDirty}
        message="Are you sure you want to leave? The changes you made will be lost."
      />
      {isCreatingTeam && (
        <TeamEditDialog
          organization={{ id: organizationId }}
          onClose={() => setIsCreatingTeam(false)}
          onTeamSaved={(team) => {
            setIsFormDirty(true);
            setIsReachChanged(true);
            setAppliesToTeams((prev) => [...prev, team]);
          }}
        />
      )}
      {isCreatingCareerTrack && (
        <CareerTrackCreateModal
          organizationId={organizationId}
          onClose={() => setIsCreatingCareerTrack(false)}
          onCareerTrackCreated={(track) => {
            setIsFormDirty(true);
            setIsReachChanged(true);
            setAppliesToCareerTracks((prev) => [...prev, track]);
          }}
        />
      )}
      {loading && (
        <div className="flex justify-center mt-8 mb-12">
          <Loading />
        </div>
      )}
      {!loading && !data?.competency && !isNew && (
        <div className="flex justify-center mt-8">
          <div className="text-gray-500">
            {label("competency", { capitalize: true })} not found
          </div>
        </div>
      )}

      {!loading && (data?.competency || isNew) && (
        <div className="flex flex-col max-h-[calc(100vh-8rem)]">
          <ModalTitle
            onClose={onDismiss ?? onCancel}
            className="px-6 pt-6 pb-3 shrink-0"
          >
            {`${!isNew ? "Edit" : "New"} ${label("competency")}`}
          </ModalTitle>
          {/* pt-1 keeps the scroll edge off the first field's focus ring. */}
          <div className="overflow-y-auto px-6 pt-1 pb-6 min-h-0">
            {formBody}
          </div>
          <div className="border-t px-6 py-4 shrink-0 flex items-center justify-between gap-2">
            {/* Deleting reaches every career track using it, so confirm. */}
            {!isNew ? (
              <Button
                size={ButtonSize.medium}
                theme={buttonTheme.redDanger}
                disabled={isDeleting || isSaving}
                text="Delete"
                onClick={handleDeleteCompetency}
              />
            ) : (
              <div />
            )}
            <Controls
              isSaving={isSaving}
              onCancel={onCancel}
              canSave={canSave}
              saveTooltip={saveTooltip}
              onSave={handleSaveCompetency}
              size={ButtonSize.medium}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CompetencyForm;
